import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import ScrollToTop from "../../../core/common/ScrollToTop";
import { fetchAllContacts, CompanyResponse } from "../../../api/contact";
import { toast } from "react-toastify";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Filler,
  ChartOptions,
  TooltipItem,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { all_routes } from "../../router/all_routes";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import Tip from "../../../core/common/tooltip";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Filler
);

type Period =
  | "global"
  | "week"
  | "thisMonth"
  | "lastMonth"
  | "threeMonths"
  | "sixMonths"
  | "thisYear"
  | "lastYear"
  | "overall";

const InfoIcon = ({ text }: { text: string }) => (
  <Tip text={text} placement="top">
    <i
      className="bi bi-info-circle ms-2 text-secondary"
      style={{ cursor: "pointer", fontSize: 15 }}
    />
  </Tip>
);

const ContactInsights = () => {
  const route = all_routes;
  const [contacts, setContacts] = useState<CompanyResponse[]>([]);
  const [loading, setLoading] = useState(true);

  // Global + Individual Filters
  const [globalPeriod, setGlobalPeriod] = useState<Period>("thisMonth");
  const [overallPeriod, setOverallPeriod] = useState<Period>("global");
  const [ownerPeriod, setOwnerPeriod] = useState<Period>("global");
  const [sourcePeriod, setSourcePeriod] = useState<Period>("global");
  const [yearPeriod, setYearPeriod] = useState<Period>("global");
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const periodOptions = [
    { value: "global" as const, label: "Use Global Filter" },
    { value: "week" as const, label: "Last 1 Week" },
    { value: "thisMonth" as const, label: "This Month" },
    { value: "lastMonth" as const, label: "Last Month" },
    { value: "threeMonths" as const, label: "Past 3 Months" },
    { value: "sixMonths" as const, label: "Past 6 Months" },
    { value: "thisYear" as const, label: "This Year" },
    // ⭐ ADDED NEW OPTIONS
    { value: "lastYear" as const, label: "Last Year" },
    { value: "overall" as const, label: "Overall" },
  ];

  // Helper: Get date range
  const getDateRange = useCallback(
    (
      period:
        | "week"
        | "thisMonth"
        | "lastMonth"
        | "threeMonths"
        | "sixMonths"
        | "thisYear"
        | "lastYear"
        | "overall"
    ) => {
      const now = new Date();
      let start: Date;
      let end: Date = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59
      );

      switch (period) {
        case "week":
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "thisMonth":
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case "lastMonth":
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
          break;
        case "threeMonths":
          start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        case "sixMonths":
          start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
          break;
        case "thisYear":
          start = new Date(now.getFullYear(), 0, 1);
          break;
        case "lastYear":
          start = new Date(now.getFullYear() - 1, 0, 1);
          end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
          break;
        case "overall":
        default:
          start = new Date(0);
          break;
      }
      return { start, end };
    },
    []
  );

  const getFilteredContacts = useCallback(
    (localPeriod: Period): CompanyResponse[] => {
      const effectivePeriod =
        localPeriod === "global" ? globalPeriod : localPeriod;
      if (effectivePeriod === "global" || effectivePeriod === "overall")
        return contacts;

      const { start, end } = getDateRange(effectivePeriod as any);
      return contacts.filter((c) => {
        const date = c.company.created_at
          ? new Date(c.company.created_at)
          : null;
        return date && date >= start && date <= end;
      });
    },
    [contacts, globalPeriod, getDateRange]
  );

  // Load Contacts
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchAllContacts();
        setContacts(data || []);
      } catch (err) {
        toast.error("Failed to load contacts");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // 1. Overall Status (Assigned vs Unassigned)
  const overallFiltered = useMemo(
    () => getFilteredContacts(overallPeriod),
    [getFilteredContacts, overallPeriod]
  );
  const overallStats = useMemo(() => {
    const assigned = overallFiltered.filter((c) => c.company.username).length;
    const unassigned = overallFiltered.length - assigned;
    return { assigned, unassigned, total: overallFiltered.length };
  }, [overallFiltered]);

  const overallDoughnutData = {
    labels: [
      `Assigned (${overallStats.assigned})`,
      `Unassigned (${overallStats.unassigned})`,
    ],
    datasets: [
      {
        data: [overallStats.assigned, overallStats.unassigned],
        backgroundColor: ["#1E88E5", "#FF9800"],

        borderWidth: 4,
        borderColor: "#fff",
      },
    ],
  };

  // 2. Lead Owner Activity
  const ownerFiltered = useMemo(
    () => getFilteredContacts(ownerPeriod),
    [getFilteredContacts, ownerPeriod]
  );
  const ownerData = useMemo(() => {
    const map: Record<
      string,
      { total: number; assigned: number; unassigned: number }
    > = {};
    ownerFiltered.forEach((c) => {
      const owner = c.company.username || "Unassigned";
      if (!map[owner]) map[owner] = { total: 0, assigned: 0, unassigned: 0 };
      map[owner].total++;
      if (c.company.username) map[owner].assigned++;
      else map[owner].unassigned++;
    });
    return Object.entries(map)
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => b.total - a.total);
  }, [ownerFiltered]);

  // 3. Lead Source Effectiveness
  const sourceFiltered = useMemo(
    () => getFilteredContacts(sourcePeriod),
    [getFilteredContacts, sourcePeriod]
  );
  const sourceData = useMemo(() => {
    const map: Record<string, { total: number; assigned: number }> = {};
    sourceFiltered.forEach((c) => {
      const src = c.company.leadsource_name || "Unknown Source";
      if (!map[src]) map[src] = { total: 0, assigned: 0 };
      map[src].total++;
      if (c.company.username) map[src].assigned++;
    });
    return Object.entries(map)
      .map(([name, d]) => ({
        name,
        total: d.total,
        assignedRate: d.total > 0 ? (d.assigned / d.total) * 100 : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [sourceFiltered]);

  // 4. Companies by Year (MoM + YoY)
  // 4. Companies by Year (MoM + YoY)
  const yearFiltered = useMemo(() => {
    const year =
      globalPeriod === "thisYear"
        ? new Date().getFullYear()
        : globalPeriod === "lastYear"
          ? new Date().getFullYear() - 1
          : selectedYear;

    const prevYear = year - 1;

    const monthly = Array(12)
      .fill(null)
      .map(() => ({ current: 0, previous: 0 }));

    contacts.forEach((c) => {
      const dateStr = c.company.created_at;
      if (!dateStr) return;
      const d = new Date(dateStr);
      const m = d.getMonth();
      const y = d.getFullYear();

      if (y === year) monthly[m].current++;
      if (y === prevYear) monthly[m].previous++;
    });

    return {
      year,
      prevYear,
      data: monthly.map((m, i) => {
        const mom =
          i === 0 || monthly[i - 1].current === 0
            ? 0
            : ((m.current - monthly[i - 1].current) / monthly[i - 1].current) *
            100;

        const yoy =
          m.previous === 0 ? 0 : ((m.current - m.previous) / m.previous) * 100;

        return {
          month: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ][i],
          current: m.current,
          previous: m.previous,
          mom: isFinite(mom) ? Number(mom.toFixed(2)) : 0,
          yoy: isFinite(yoy) ? Number(yoy.toFixed(2)) : 0,
        };
      }),
    };
  }, [contacts, globalPeriod, selectedYear]);
  const companiesByYearChart = {
    labels: yearFiltered.data.map((d) => d.month),
    datasets: [
      {
        label: yearFiltered.year.toString(),
        data: yearFiltered.data.map((d) => d.current),
        borderColor: "#2196F3",
        backgroundColor: "rgba(33, 150, 243, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: yearFiltered.prevYear.toString(),
        data: yearFiltered.data.map((d) => d.previous),
        borderColor: "#9E9E9E",
        borderDash: [5, 5],
      },
    ],
  };

  const renderFilter = (value: Period, setter: (v: Period) => void) => (
    <Dropdown>
      <Dropdown.Toggle variant="outline-secondary" size="sm">
        {periodOptions.find((o) => o.value === value)?.label || "Select Period"}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {periodOptions.map((opt) => (
          <Dropdown.Item key={opt.value} onClick={() => setter(opt.value)}>
            {opt.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-body">
          <div className="text-center py-5">
            <i className="ti ti-loader-3 ti-spin fs-48 text-primary"></i>
            <p className="mt-3">Loading Contact Insights...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="card shadow-sm border-0 rounded-4 p-4 mb-4 bg-light-subtle">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h3 className="mb-1 fw-semibold d-flex align-items-center gap-2">
                  <i className="bi bi-building fs-3 text-primary"></i>
                  Contact Insights Dashboard
                  <InfoIcon text="Full overview of contact assignment, owners, sources and yearly activity" />
                </h3>

                <p className="text-muted mb-1">
                  Complete analytics overview of all companies & contacts
                </p>

                {/* Breadcrumb */}
                <ol className="breadcrumb mb-0 small">
                  <li className="breadcrumb-item">
                    <Link to={route.contactList}  
                    style={{
                        color: "#9595b5",
                        textDecoration: "none",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#E41F07")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#9595b5")
                      }>
                      Contacts
                    </Link>
                  </li>
                  <li className="breadcrumb-item active">Contact Insights</li>
                </ol>
                
              </div>
              <div className="col-md-4 text-md-end">
                <div className="d-inline-flex align-items-center gap-2 bg-white shadow-sm border rounded px-3 py-2">
                  <i className="bi bi-funnel text-primary"></i>
                  <strong className="small">Global Filter:</strong>
                  {renderFilter(globalPeriod, setGlobalPeriod)}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {/* Overall Status */}
            <div className="col-xl-6 col-md-6">
              <div className="card h-100 shadow-sm border-0 rounded-4">
                <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-people-fill text-primary"></i>
                    Contact Assignment Status
                    <InfoIcon text="Shows assigned vs unassigned contacts for the selected period" />
                  </h5>

                  <div
                    style={{
                      transform: "scale(0.9)",
                      transformOrigin: "right center",
                    }}
                  >
                    {renderFilter(overallPeriod, setOverallPeriod)}
                  </div>
                </div>


                <div className="card-body">
                  {contacts.length === 0 || overallStats.total === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-lines-fill text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for contact insights
                      </p>
                    </div>
                  ) : (
                    <>
                      <div
                        className="mx-auto"
                        style={{ width: "350px", height: "350px" }}
                      >
                        <Doughnut
                          data={overallDoughnutData}
                          options={{
                            responsive: true,
                            plugins: { legend: { position: "bottom" } },
                          }}
                        />
                      </div>

                      <div className="text-center mt-3">
                        <h4>{overallStats.total.toLocaleString()}</h4>
                        <small className="text-muted">Total Contacts</small>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Lead Owner Activity */}
            <div className="col-xl-6 col-md-6">
              <div className="card h-100 shadow-sm border-0 rounded-4">
                {/* Header */}
                <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-people text-primary me-2"></i>
                    Lead Owner Activity
                    <InfoIcon text="Displays contact distribution and assignment stats for each owner" />
                  </h5>

                  <div style={{ transform: "scale(0.9)", transformOrigin: "right center" }}>
                    {renderFilter(ownerPeriod, setOwnerPeriod)}
                  </div>

                </div>

                <div className="card-body">
                  {contacts.length === 0 || ownerData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-lines-fill text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for contact insights
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Enhanced Summary Row */}
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom small text-muted">
                        {/* Total Owners */}
                        <div>
                          <strong className="text-primary">
                            {ownerData.length}
                          </strong>{" "}
                          Owners
                        </div>

                        {/* Total Contacts */}
                        <div>
                          <strong className="text-info">
                            {ownerData.reduce((s, x) => s + x.total, 0)}
                          </strong>{" "}
                          Total Contacts
                        </div>

                        {/* Total Assigned */}
                        <div>
                          <strong className="text-success">
                            {ownerData.reduce((s, x) => s + x.assigned, 0)}
                          </strong>{" "}
                          Assigned
                        </div>

                        {/* Total Unassigned */}
                        <div>
                          <strong className="text-warning">
                            {ownerData.reduce((s, x) => s + x.unassigned, 0)}
                          </strong>{" "}
                          Unassigned
                        </div>
                      </div>

                      {/* Bar Chart */}
                      <div style={{ height: 350 }}>
                        <Bar
                          data={{
                            labels: ownerData.map((o) => o.name),
                            datasets: [
                              {
                                label: "Assigned",
                                data: ownerData.map((o) => o.assigned),
                                backgroundColor: "#00A8E8",
                                borderRadius: 5,
                                barThickness: "flex",
                              },
                              {
                                label: "Unassigned",
                                data: ownerData.map((o) => o.unassigned),
                                backgroundColor: "#FF9800",
                                borderRadius: 5,
                                barThickness: "flex",
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              x: {
                                stacked: true,
                                ticks: { maxRotation: 0, minRotation: 0 },
                              },
                              y: {
                                stacked: true,
                                beginAtZero: true,
                              },
                            },
                            plugins: {
                              legend: {
                                display: true, // 🔥 Chart.js legend re-enabled
                                position: "top",
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (ctx) {
                                    const assigned =
                                      ownerData[ctx.dataIndex]?.assigned || 0;
                                    const unassigned =
                                      ownerData[ctx.dataIndex]?.unassigned || 0;
                                    const total = assigned + unassigned;

                                    return `${ctx.dataset.label}: ${ctx.formattedValue} (Total: ${total})`;
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </div>

                      {/* Custom Badge Legend */}
                      <div className="mt-3 text-center">
                        <span className="badge bg-success-subtle text-success me-2 px-3 py-2">
                          <span className="badge-dot bg-success me-1"></span>
                          Assigned
                        </span>
                        <span className="badge bg-warning-subtle text-warning px-3 py-2">
                          <span className="badge-dot bg-warning me-1"></span>
                          Unassigned
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Lead Source Effectiveness */}
            <div className="col-xl-6">
              <div className="card shadow-sm border-0 rounded-4">
                {/* Header */}
                <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-graph-up-arrow text-primary me-2"></i>
                    Lead Source Effectiveness
                    <InfoIcon text="Shows performance of each lead source and average assignment rate" />
                  </h5>

                  {renderFilter(sourcePeriod, setSourcePeriod)}
                </div>

                <div className="card-body">
                  {contacts.length === 0 || sourceData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-lines-fill text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for contact insights
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* SUMMARY ROW */}
                      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom small text-muted">
                        <div>
                          <strong className="text-primary">
                            {sourceData.length}
                          </strong>{" "}
                          Sources
                        </div>

                        <div>
                          <strong className="text-info">
                            {sourceData.reduce((s, x) => s + x.total, 0)}
                          </strong>{" "}
                          Total Contacts
                        </div>

                        <div>
                          <strong className="text-success">
                            {(
                              sourceData.reduce((s, x) => s + x.assignedRate, 0) /
                              sourceData.length
                            ).toFixed(1)}
                            %
                          </strong>{" "}
                          Avg Assigned
                        </div>
                      </div>

                      {/* SOURCE LIST */}
                      {sourceData.map((s, i) => {
                        const hue = (i * 360) / sourceData.length;
                        const assignedColor = `hsl(${hue}, 70%, 45%)`;
                        const assignedBG = `hsl(${hue}, 70%, 90%)`;

                        return (
                          <div key={s.name} className="mb-4">
                            {/* Header Row */}
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="fw-semibold">{s.name}</span>
                              <span className="text-muted small">
                                {s.total} Contacts • {s.assignedRate.toFixed(1)}%
                              </span>
                            </div>

                            {/* Progress Bar Container */}
                            <div
                              className="rounded-3 shadow-sm"
                              style={{
                                background: "#f1f1f1",
                                height: "22px",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                className="h-100 d-flex align-items-center text-white fw-bold"
                                style={{
                                  width: `${s.assignedRate}%`,
                                  background: `linear-gradient(90deg, ${assignedColor}, hsl(${hue}, 70%, 35%))`,
                                  paddingLeft: 8,
                                  fontSize: "12px",
                                }}
                              >
                                {s.assignedRate.toFixed(0)}%
                              </div>
                            </div>

                            {/* Footer badges */}
                            <div className="d-flex justify-content-between mt-2 small">
                              <span className="badge bg-success-subtle text-success px-2 py-1">
                                Assigned:{" "}
                                {Math.round((s.assignedRate / 100) * s.total)}
                              </span>
                              <span className="badge bg-warning-subtle text-warning px-2 py-1">
                                Unassigned:{" "}
                                {s.total -
                                  Math.round((s.assignedRate / 100) * s.total)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Companies by Year */}
            <div className="col-xl-6">
              <div className="card shadow-sm border-0 rounded-4">
                {/* Header */}
                <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-calendar3 text-primary me-2"></i>
                    Companies by Year
                    <InfoIcon text="Year-wise company creation trend with MoM and YoY growth metrics" />
                  </h5>

                  <select
                    className="form-select form-select-sm w-auto"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(+e.target.value)}
                  >
                    {[0, 1, 2].map((i) => {
                      const y = new Date().getFullYear() - i;
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="card-body">
                  {contacts.length === 0 || yearFiltered.data.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-person-lines-fill text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                      <p className="text-muted mb-0 fw-semibold">
                        No data found for contact insights
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Summary KPIs */}
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom small text-muted">
                        {/* Total contacts this year */}
                        <div>
                          <strong className="text-primary">
                            {yearFiltered.data.reduce((s, x) => s + x.current, 0)}
                          </strong>{" "}
                          Total Contacts
                        </div>

                        {/* MoM average */}
                        <div>
                          <strong
                            className={
                              yearFiltered.data.reduce((s, x) => s + x.mom, 0) >= 0
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {(
                              yearFiltered.data.reduce((s, x) => s + x.mom, 0) /
                              yearFiltered.data.length
                            ).toFixed(1)}
                            %
                          </strong>{" "}
                          Avg MoM
                        </div>

                        {/* YoY average */}
                        <div>
                          <strong
                            className={
                              yearFiltered.data.reduce((s, x) => s + x.yoy, 0) >= 0
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {(
                              yearFiltered.data.reduce((s, x) => s + x.yoy, 0) /
                              yearFiltered.data.length
                            ).toFixed(1)}
                            %
                          </strong>{" "}
                          Avg YoY
                        </div>
                      </div>

                      {/* CHART */}
                      <div style={{ height: 320 }}>
                        <Line
                          data={companiesByYearChart}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { position: "top" },
                              tooltip: {
                                callbacks: {
                                  label: function (ctx) {
                                    return `${ctx.dataset.label}: ${ctx.raw} contacts`;
                                  },
                                },
                              },
                            },
                            scales: {
                              y: { beginAtZero: true },
                            },
                          }}
                        />
                      </div>

                      {/* TABLE */}
                      <div className="table-responsive">
                        <table className="table table-sm table-hover align-middle">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Contacts</th>
                              <th>MoM</th>
                              <th>YoY</th>
                            </tr>
                          </thead>

                          <tbody>
                            {yearFiltered.data.map((m) => (
                              <tr key={m.month}>
                                {/* Month */}
                                <td>{m.month}</td>

                                {/* Contacts */}
                                <td>
                                  <strong className="text-dark">{m.current}</strong>
                                </td>

                                {/* MoM */}
                                <td
                                  className={
                                    m.mom >= 0
                                      ? "text-success fw-semibold"
                                      : "text-danger fw-semibold"
                                  }
                                >
                                  {m.mom >= 0 ? (
                                    <i className="bi bi-arrow-up-right"></i>
                                  ) : (
                                    <i className="bi bi-arrow-down-right"></i>
                                  )}{" "}
                                  {m.mom.toFixed(1)}%
                                </td>

                                {/* YoY */}
                                <td
                                  className={
                                    m.yoy >= 0
                                      ? "text-success fw-semibold"
                                      : "text-danger fw-semibold"
                                  }
                                >
                                  {m.yoy >= 0 ? (
                                    <i className="bi bi-graph-up-arrow"></i>
                                  ) : (
                                    <i className="bi bi-graph-down-arrow"></i>
                                  )}
                                  {m.yoy.toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default ContactInsights;
